import React from 'react'
import { Badge, Button, Card, CardContent, CardHeader, useToast } from '@/shared/ui'
import { Download, CheckCircle, AlertCircle } from 'lucide-react'
import { usePwaInstall } from '@/modules/pwa'
import { isPwaInstalled } from '@/modules/pwa/services/webpushClient'
import { getPlatformInfo } from '@/modules/pwa/utils/platform'
import InstallInstructionsModal from './InstallInstructionsModal'
import { manageAppAnchors } from '@/modules/pwa/utils/manageAppAnchors'

export default function InstallStatusCard() {
  const { canInstall, installing, installed: installedFromEvent, isIosSafari, promptInstall } = usePwaInstall()
  const { add: toast } = useToast()
  const platform = React.useMemo(() => getPlatformInfo(), [])
  const installed = installedFromEvent || isPwaInstalled()
  const [showInstructions, setShowInstructions] = React.useState(false)
  const canPromptInstall = canInstall && !installed && !isIosSafari
  const showManual = !installed && !canPromptInstall

  const statusLabel = installed ? 'Instalado' : 'Nao instalado'
  const statusColor = installed ? 'success' : 'warning'
  const statusIcon = installed ? <CheckCircle className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />

  async function onInstall() {
    if (canPromptInstall) {
      try { await promptInstall() } catch {}
      return
    }
    if (isIosSafari) {
      setShowInstructions(true)
      return
    }
    toast({
      title: 'Instalacao',
      description: 'O navegador ainda nao liberou o prompt de instalacao. Veja o diagnostico e as instrucoes abaixo.',
      variant: 'warning',
      soft: true,
    })
    try {
      document.getElementById(manageAppAnchors.installabilityDiagnostics)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    } catch {}
  }

  return (
    <Card>
      <CardHeader className="text-sm font-medium">Status da instalacao</CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          <Badge variant="soft" color={statusColor} className="gap-1">
            {statusIcon}
            {statusLabel}
          </Badge>
          {installed && <span className="text-xs text-foreground/70">App pronto para uso.</span>}
        </div>
        {!installed && (
          <p className="text-sm text-foreground/80">
            Instale o app para acesso rapido e melhor experiencia no desktop e no celular.
          </p>
        )}
        {!installed && (
          <div>
            <Button className="h-11" onClick={onInstall} disabled={canPromptInstall ? installing : false}>
              <Download className="mr-2 h-4 w-4" />
              {canPromptInstall ? (installing ? 'Instalando...' : 'Instalar app') : 'Instalar app'}
            </Button>
          </div>
        )}
        {showManual && (
          <div className="text-xs text-foreground/70">
            {platform.isIos
              ? 'No iOS, use Compartilhar > Adicionar a Tela de Inicio.'
              : 'Se o botao de instalar nao aparecer, use o app por alguns minutos (heuristica do navegador) e recarregue.'}
          </div>
        )}
      </CardContent>
      <InstallInstructionsModal open={showInstructions} onOpenChange={setShowInstructions} />
    </Card>
  )
}
